// import { isNil } from '@/utils/helpers'
import useLocalStorage from '@/utils/localStorage'
import { useSearchParams } from 'next/navigation'
import { FunctionComponent, useContext, createContext, useEffect } from 'react'

const themeContext = createContext<ThemeContext>({
  theme: 'light',
  setTheme: (theme) => {
    throw new Error('setTheme not implemented')
  }
})

export const ThemeProvider: FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useLocalStorage<AppTheme>('@mobicom_theme', 'light')
  // const prefersDarkMode = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches

  // let currentTheme = prefersDarkMode ? 'dark' : (!isNil(theme) && (theme === 'light' || theme === 'dark')) ? theme : 'light'

  // let currentTheme = (!isNil(theme) && (theme === 'light' || theme === 'dark')) ? theme : 'light'

  const mode = useSearchParams().get('theme')

  /**
   * URL-based theme (?theme=dark)
   * Keeps your original logic intact
   */
  useEffect(() => {
    if (mode !== null && (mode === 'light' || mode === 'dark' || mode === 'kaosdark' || mode === 'kaoslight')) {
      setTheme(mode)
      document.body.classList.replace(theme, mode)
      // document.body.classList.add(mode)
    }
  }, [mode])

  /**
   * Apply theme class to BODY (original behavior)
   */
  useEffect(() => {
    if (theme === 'light' || theme === 'dark' || theme === 'kaosdark' || theme === 'kaoslight') {
      document.body.classList.add(theme)
    }
  }, [theme])

  /**
   * ✅ CRITICAL FIX
   * Sync body.dark → html.dark so Tailwind dark mode works
   * This allows you to keep existing AppBar logic unchanged
   */
  useEffect(() => {
    const root = document.documentElement
    const body = document.body

    const syncTheme = () => {
      if (body.classList.contains('dark')) {
        root.classList.add('dark')
        root.classList.remove('light')
        root.classList.remove('kaos-light')
        root.classList.remove('kaos-dark')
      } else if (body.classList.contains('kaosdark')) {
        root.classList.add('kaosdark')
        root.classList.remove('light')
        root.classList.remove('dark')
        root.classList.remove('kaos-light')
      } else if (body.classList.contains('kaoslight')) {
        root.classList.add('kaoslight')
        root.classList.remove('light')
        root.classList.remove('dark')
        root.classList.remove('kaos-dark')
      } else {
        root.classList.add('light')
        root.classList.remove('dark')
        root.classList.remove('kaos-light')
        root.classList.remove('kaos-dark')
      }
    }

    // Initial sync
    syncTheme()

    // Observe body class changes
    const observer = new MutationObserver(syncTheme)
    observer.observe(body, { attributes: true, attributeFilter: ['class'] })

    return () => observer.disconnect()
  }, [])

  return (
    <themeContext.Provider
      value={{
        theme,
        setTheme
      }}
    >
      {children}
    </themeContext.Provider>
  )
}

export const useTheme = (): ThemeContext => {
  return useContext(themeContext)
}

export interface ThemeContext {
  theme: AppTheme
  setTheme: (theme: AppTheme) => void
}

export type AppTheme = 'light' | 'dark' | 'kaosdark' | 'kaoslight'

// export interface Language {
//   locale: Locale
//   name: string
//   icon: string
// }
